import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit, Optional, ViewChild } from '@angular/core';
import { IFilterFacet, IFilterFacetItem } from '@impact/data';

import { checkmarkIcon, chevronDownIcon } from '../../../icons';
import { FacetStateService } from '../../core/facet-state.service';
import { ExpansionPanelComponent } from '../../expansion-panel/expansion-panel.component';
import { FilterService } from '../../filter-service/filter.service';
import { COLLAPSE_PANELS } from '../../models/filter.models';
import { sortByKey } from '../../core/helpers';

enum FacetOptions {
    BodyType = 'bodyTypes.keyword',
}

type CarType =
    | '7 personers biler'
    | 'Cabriolet'
    | 'Coupé'
    | 'El'
    | 'Hatchback'
    | 'Hybrid & el'
    | 'Hybrid'
    | 'Lille bil'
    | 'Sedan'
    | 'Stationcar'
    | 'SUV'
    | 'Van'
    | 'Varevogn';

@Component({
    selector: 'app-facet',
    template: `
        <div class="facet" *ngIf="facetItems.length">
            <app-expansion-panel #panel [headerClickable]="true" [expanded]="expandPanel" (changed)="onPanelChange($event)">
                <h6 class="facet__title" expansion-title [class.facet__title--no-margin]="!panel.animating && !panel.expanded">
                    {{ facet?.label }}
                </h6>
                <ng-container expansion-icon>${chevronDownIcon}</ng-container>
                <ul class="facet__list">
                    <li *ngFor="let facetOption of facetItems">
                        <label class="facet__list-item animate-fade-in">
                            <img
                                class="facet__image"
                                *ngIf="isBodyType"
                                [src]="getBodyTypeImage(facetOption.key)"
                                [alt]="facetOption.key"
                                loading="lazy"
                                width="55"
                                height="20"
                            />
                            <span class="facet__label-text"> {{ facetOption.label }} ({{ facetOption.count }}) </span>
                            <div class="checkbox">
                                <input
                                    type="checkbox"
                                    [id]="facetOption.key"
                                    [checked]="facetOption.selected"
                                    (change)="handleChange(facetOption)"
                                />
                                <span class="checkbox__checkmark"> ${checkmarkIcon} </span>
                            </div>
                        </label>
                    </li>
                </ul>
                <button *ngIf="showMoreToggleIsVisible()" class="button--as-link facet__show-more-toggle" (click)="onShowMoreToggle()">
                    <div *ngIf="!showingMore; else showingLess">{{ 'generic.show_more' | translate }}</div>
                    <ng-template #showingLess>{{ 'generic.show_less' | translate }}</ng-template>
                </button>
            </app-expansion-panel>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacetComponent implements OnInit {
    @Input() expandInitially?: boolean;
    @Input() facetKey: string;

    @Input()
    get facet() {
        return this._facet;
    }
    set facet(value: IFilterFacet) {
        this._facet = value;
        this.updateFacetItems();
    }
    private _facet: IFilterFacet;

    @ViewChild('panel') panel: ExpansionPanelComponent;

    isBodyType = false;
    showingMore = false;
    facetItems: IFilterFacetItem[] = [];

    expandPanel = true;

    private itemsSubsetSize = 6;

    constructor(
        public readonly filterService: FilterService,
        private readonly facetStateService: FacetStateService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(COLLAPSE_PANELS) private collapsePanels: boolean
    ) {}

    ngOnInit(): void {
        if (!this.facet.label) {
            return;
        }

        const expandState = this.facetStateService.getExpandedState(this.facet.label);
        this.expandPanel = expandState ?? this.expandInitially ?? this.collapsePanels ?? false;

        this.isBodyType = this.facetKey === FacetOptions.BodyType;
        this.showingMore = this.facetStateService.getShowMoreState(this.facet.label);
        this.updateFacetItems();
    }

    handleChange(option: IFilterFacetItem) {
        this.filterService.setFacet({ facet: this.facetKey, option });
    }

    onShowMoreToggle() {
        if (!this.facet.label) {
            return;
        }
        if (this.showingMore) {
            this.facetStateService.storeFacetAsShowingLess(this.facet.label);
        } else {
            this.facetStateService.storeFacetAsShowingMore(this.facet.label);
        }

        this.showingMore = !this.showingMore;
        this.updateFacetItems();
    }

    updateFacetItems() {
        this.facetItems = [];

        if (this._facet.items) {
            if (this.showingMore) {
                // Sort by label (name), in alphabetical order, putting any selected facets at the top regardless of alphebetical order
                this.facetItems = sortByKey(this._facet.items, 'label', false).sort((a, b) => b.selected - a.selected);
            } else {
                // Sort by facet count, putting any selected facets at the top regardless of count order
                this.facetItems = sortByKey(this._facet.items, 'count', true).sort((a, b) => b.selected - a.selected);
                this.facetItems = this.facetItems.slice(0, this.itemsSubsetSize);
            }

            this.cdr.markForCheck();
        }
    }

    showMoreToggleIsVisible() {
        return (this.facet.items ?? []).length > this.itemsSubsetSize;
    }

    getBodyTypeImage(bodyType?: string | null) {
        let bodyTypeImage = 'assets/images/car-types';

        if (!bodyType) {
            return `${bodyTypeImage}/fallback`;
        }

        const carType = bodyType as CarType;

        switch (carType) {
            case 'Cabriolet':
                bodyTypeImage += '/cabriolet';
                break;
            case 'Hatchback':
                bodyTypeImage += '/hatchback';
                break;
            case 'Hybrid & el':
                bodyTypeImage += '/hybrid-and-electric';
                break;
            case 'Hybrid':
                bodyTypeImage += '/hybrid';
                break;
            case 'El':
                bodyTypeImage += '/electric';
                break;
            case 'Lille bil':
                bodyTypeImage += '/small-car';
                break;
            case 'SUV':
                bodyTypeImage += '/suv';
                break;
            case 'Stationcar':
                bodyTypeImage += '/station-car';
                break;
            case 'Van':
                bodyTypeImage += '/small-van';
                break;
            case 'Varevogn':
                bodyTypeImage += '/big-van';
                break;
            case 'Sedan':
                bodyTypeImage += '/sedan';
                break;
            case 'Coupé':
                bodyTypeImage += '/coupe';
                break;
            case '7 personers biler':
                bodyTypeImage += '/sevenseater';
                break;
            default:
                bodyTypeImage += '/fallback';
                break;
        }

        return (bodyTypeImage += '.png');
    }

    onPanelChange(value: boolean) {
        if (!this.facet.label) {
            return;
        }

        if (value) {
            this.facetStateService.storeFacetAsExpanded(this.facet.label);
        } else {
            this.facetStateService.storeFacetAsColapsed(this.facet.label);
        }
    }
}
